// UK wall-clock helpers. Every forecast hour is an absolute instant (epoch ms);
// these turn instants into Europe/London dates and labels, so BST/GMT changes
// (including the repeated 01:00 when the clocks go back) are handled by Intl,
// not by adding offsets.

export const UK_TIME_ZONE = 'Europe/London';

export const HOUR_MS = 3_600_000;

const partsFormat = new Intl.DateTimeFormat('en-GB', {
  timeZone: UK_TIME_ZONE,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  weekday: 'long',
  hourCycle: 'h23',
  timeZoneName: 'short',
});

export interface UkParts {
  /** Local date, "2026-10-25". */
  date: string;
  hour: number;
  minute: number;
  /** "Monday" */
  weekday: string;
  /** "BST" or "GMT" */
  zone: string;
}

export function ukParts(ms: number): UkParts {
  const parts: Record<string, string> = {};
  for (const part of partsFormat.formatToParts(new Date(ms))) parts[part.type] = part.value;
  return {
    date: `${parts.year}-${parts.month}-${parts.day}`,
    hour: Number(parts.hour) % 24,
    minute: Number(parts.minute),
    weekday: parts.weekday ?? '',
    zone: parts.timeZoneName === 'GMT+1' ? 'BST' : (parts.timeZoneName ?? 'GMT'),
  };
}

/** The start of the (UTC-aligned, so also UK-aligned) hour containing ms. */
export const startOfHour = (ms: number) => Math.floor(ms / HOUR_MS) * HOUR_MS;

/** UK local date of an instant, "2026-10-25". */
export const ukDate = (ms: number) => ukParts(ms).date;

/** The UK date after `date` ("2026-10-25" → "2026-10-26"). */
export function nextDate(date: string): string {
  const d = new Date(`${date}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + 1);
  return d.toISOString().slice(0, 10);
}

function hour12(hour: number): { n: number; suffix: 'am' | 'pm' } {
  return { n: hour % 12 === 0 ? 12 : hour % 12, suffix: hour < 12 ? 'am' : 'pm' };
}

/** "5pm", "12am". */
export function hourLabel(ms: number): string {
  const { n, suffix } = hour12(ukParts(ms).hour);
  return `${n}${suffix}`;
}

/** "17:42" */
export function clockLabel(ms: number): string {
  const p = ukParts(ms);
  return `${String(p.hour).padStart(2, '0')}:${String(p.minute).padStart(2, '0')}`;
}

/**
 * A time range in wall-clock hours: "5–7pm", "11am–1pm", "10pm–midnight".
 * `end` is exclusive (the start of the hour after the last one).
 */
export function hourRangeLabel(start: number, end: number): string {
  const a = hour12(ukParts(start).hour);
  const endHour = ukParts(end).hour;
  if (endHour === 0) return `${a.n}${a.suffix}–midnight`;
  const b = hour12(endHour);
  return a.suffix === b.suffix ? `${a.n}–${b.n}${b.suffix}` : `${a.n}${a.suffix}–${b.n}${b.suffix}`;
}

/** "1h 40m", "25m", "3h". */
export function durationLabel(minutes: number): string {
  const m = Math.max(0, Math.round(minutes));
  const h = Math.floor(m / 60);
  const rest = m % 60;
  if (h === 0) return `${rest}m`;
  return rest === 0 ? `${h}h` : `${h}h ${rest}m`;
}

/**
 * Hour labels for a run of consecutive hours. When the clocks go back the
 * 01:00 hour happens twice; both get the zone ("1am BST", "1am GMT") so the
 * strip never shows two identical labels side by side.
 */
export function hourLabels(times: readonly number[]): string[] {
  const labels = times.map(hourLabel);
  const seen = new Map<string, number[]>();
  times.forEach((t, i) => {
    const key = `${ukDate(t)} ${labels[i]}`;
    seen.set(key, [...(seen.get(key) ?? []), i]);
  });
  for (const indices of seen.values()) {
    if (indices.length < 2) continue;
    for (const i of indices) labels[i] = `${labels[i]} ${ukParts(times[i] ?? 0).zone}`;
  }
  return labels;
}
